import type { CatalogDraftWrite } from "@gobro/contracts";
import type { CatalogItem, Locale, MerchantWorkspace, WorkspaceCatalog } from "./domain";

export interface EditableOption {
  key: string;
  nameAr: string;
  nameFr: string;
  priceDeltaMillimes: number;
}

export interface EditableGroup {
  key: string;
  maxSelections: number;
  minSelections: number;
  nameAr: string;
  nameFr: string;
  options: EditableOption[];
}

export interface EditableItem {
  descriptionAr: string;
  descriptionFr: string;
  imageUrl: string;
  key: string;
  modifierGroupKeys: string[];
  nameAr: string;
  nameFr: string;
  priceMillimes: number;
  sku: string;
}

export interface EditableCategory {
  items: EditableItem[];
  key: string;
  nameAr: string;
  nameFr: string;
}

export interface EditableDraft {
  categories: EditableCategory[];
  modifierGroups: EditableGroup[];
}

export interface PublishDiff {
  addedCategories: string[];
  addedItems: string[];
  changedGroups: number;
  changedItems: string[];
  removedCategories: string[];
  removedItems: string[];
}

export function newKey(prefix: "category" | "group" | "item" | "option"): string {
  return `${prefix}-${crypto.randomUUID().replaceAll("-", "").slice(0, 12)}`;
}

export function draftFromWorkspace(workspace: MerchantWorkspace): EditableDraft {
  const source =
    workspace.catalogs.find((catalog) => catalog.status === "draft") ??
    workspace.catalogs.find((catalog) => catalog.status === "published");
  return source ? editableFromCatalog(source) : { categories: [], modifierGroups: [] };
}

export function draftSignature(draft: EditableDraft): string {
  return JSON.stringify(buildDraftPayload(draft, 0));
}

export function buildDraftPayload(
  draft: EditableDraft,
  expectedVersion: number,
): CatalogDraftWrite {
  const payload = {
    categories: draft.categories.map((category) => ({
      items: category.items.map((item) => ({
        description: {
          ar: localizedText(item.descriptionAr),
          fr: localizedText(item.descriptionFr),
        },
        imageUrl: item.imageUrl.trim() ? item.imageUrl.trim() : null,
        key: item.key,
        modifierGroupKeys: item.modifierGroupKeys,
        name: localizedName(item.nameAr, item.nameFr),
        priceMillimes: Math.round(item.priceMillimes),
        sku: item.sku.trim() ? item.sku.trim() : null,
      })),
      key: category.key,
      name: localizedName(category.nameAr, category.nameFr),
    })),
    expectedVersion,
    modifierGroups: draft.modifierGroups.map((group) => ({
      key: group.key,
      maxSelections: group.maxSelections,
      minSelections: group.minSelections,
      name: localizedName(group.nameAr, group.nameFr),
      options: group.options.map((option) => ({
        key: option.key,
        name: localizedName(option.nameAr, option.nameFr),
        priceDeltaMillimes: Math.round(option.priceDeltaMillimes),
      })),
    })),
  };
  return payload as CatalogDraftWrite;
}

export function validateDraft(draft: EditableDraft, locale: Locale): string[] {
  const issues: string[] = [];
  const push = (ar: string, fr: string) => issues.push(locale === "ar" ? ar : fr);
  if (draft.categories.length === 0) {
    push("أضف فئة واحدة على الأقل قبل الحفظ", "Ajoutez au moins une catégorie avant d’enregistrer");
  }
  for (const category of draft.categories) {
    const label = category.nameAr.trim() || category.key;
    if (!category.nameAr.trim()) {
      push("لكل فئة اسم عربي إجباري", "Chaque catégorie exige un nom arabe");
    }
    if (category.items.length === 0) {
      push(
        `الفئة «${label}» فارغة، أضف صنفًا أو احذفها`,
        `La catégorie « ${label} » est vide, ajoutez un article ou supprimez-la`,
      );
    }
    for (const item of category.items) {
      if (!item.nameAr.trim()) {
        push(`صنف بلا اسم في فئة «${label}»`, `Article sans nom dans « ${label} »`);
      }
      if (!Number.isFinite(item.priceMillimes) || item.priceMillimes < 0) {
        push(
          `سعر غير صالح للصنف «${item.nameAr.trim() || item.key}»`,
          `Prix invalide pour « ${item.nameAr.trim() || item.key} »`,
        );
      }
      const imageUrl = item.imageUrl.trim();
      if (imageUrl && !imageUrl.startsWith("https://")) {
        push(
          `رابط الصورة يجب أن يبدأ بـ https:// للصنف «${item.nameAr.trim() || item.key}»`,
          `L’URL de l’image doit commencer par https:// pour « ${item.nameAr.trim() || item.key} »`,
        );
      }
    }
  }
  for (const group of draft.modifierGroups) {
    const label = group.nameAr.trim() || group.key;
    if (!group.nameAr.trim()) {
      push("لمجموعة الخيارات اسم عربي إجباري", "Chaque groupe d’options exige un nom arabe");
    }
    if (group.options.length === 0) {
      push(`مجموعة «${label}» بلا خيارات`, `Le groupe « ${label} » n’a aucune option`);
    }
    if (group.minSelections > group.maxSelections) {
      push(
        `الحد الأدنى أكبر من الأقصى في «${label}»`,
        `Le minimum dépasse le maximum dans « ${label} »`,
      );
    }
    if (group.maxSelections > group.options.length) {
      push(
        `الحد الأقصى أكبر من عدد الخيارات في «${label}»`,
        `Le maximum dépasse le nombre d’options dans « ${label} »`,
      );
    }
    for (const option of group.options) {
      if (!option.nameAr.trim()) {
        push(`خيار بلا اسم في «${label}»`, `Option sans nom dans « ${label} »`);
      }
      if (!Number.isFinite(option.priceDeltaMillimes) || option.priceDeltaMillimes < 0) {
        push(`فرق سعر غير صالح في «${label}»`, `Delta de prix invalide dans « ${label} »`);
      }
    }
  }
  return issues;
}

export function diffDraft(draft: EditableDraft, published: WorkspaceCatalog | null): PublishDiff {
  const diff: PublishDiff = {
    addedCategories: [],
    addedItems: [],
    changedGroups: 0,
    changedItems: [],
    removedCategories: [],
    removedItems: [],
  };
  if (!published) {
    return {
      ...diff,
      addedCategories: draft.categories.map((category) => category.nameAr.trim() || category.key),
      addedItems: draft.categories.flatMap((category) =>
        category.items.map((item) => item.nameAr.trim() || item.key),
      ),
      changedGroups: draft.modifierGroups.length,
    };
  }
  const publishedCategories = new Map(
    published.content.categories.map((category) => [entityKey(category), category]),
  );
  const publishedItems = new Map<string, CatalogItem>();
  for (const category of published.content.categories) {
    for (const item of category.items) publishedItems.set(entityKey(item), item);
  }
  const publishedGroups = new Map(
    published.content.modifierGroups.map((group) => [entityKey(group), group]),
  );
  const groupKeyById = new Map(
    published.content.modifierGroups.map((group) => [group.id, entityKey(group)]),
  );

  for (const category of draft.categories) {
    if (!publishedCategories.has(category.key)) {
      diff.addedCategories.push(category.nameAr.trim() || category.key);
    }
    for (const item of category.items) {
      const match = publishedItems.get(item.key);
      if (!match) {
        diff.addedItems.push(item.nameAr.trim() || item.key);
      } else if (itemChanged(item, match, groupKeyById)) {
        diff.changedItems.push(item.nameAr.trim() || item.key);
      }
    }
  }
  for (const category of published.content.categories) {
    const key = entityKey(category);
    if (!draft.categories.some((candidate) => candidate.key === key)) {
      diff.removedCategories.push(category.name.ar ?? category.name.fr ?? key);
    }
  }
  const draftItemKeys = new Set(
    draft.categories.flatMap((category) => category.items.map((item) => item.key)),
  );
  for (const [key, item] of publishedItems) {
    if (!draftItemKeys.has(key)) diff.removedItems.push(item.name.ar ?? item.name.fr ?? key);
  }
  const draftGroupKeys = new Set(draft.modifierGroups.map((group) => group.key));
  for (const group of draft.modifierGroups) {
    const match = publishedGroups.get(group.key);
    if (!match || groupChanged(group, match)) diff.changedGroups += 1;
  }
  for (const key of publishedGroups.keys()) {
    if (!draftGroupKeys.has(key)) diff.changedGroups += 1;
  }
  return diff;
}

export function publishedItemMap(published: WorkspaceCatalog | null): Map<string, CatalogItem> {
  const map = new Map<string, CatalogItem>();
  if (!published) return map;
  for (const category of published.content.categories) {
    for (const item of category.items) map.set(entityKey(item), item);
  }
  return map;
}

export function itemChangeKind(
  item: EditableItem,
  publishedItems: Map<string, CatalogItem>,
  published: WorkspaceCatalog | null,
): "modified" | "new" | null {
  const match = publishedItems.get(item.key);
  if (!match) return "new";
  const groupKeyById = new Map(
    (published?.content.modifierGroups ?? []).map((group) => [group.id, entityKey(group)]),
  );
  return itemChanged(item, match, groupKeyById) ? "modified" : null;
}

function editableFromCatalog(catalog: WorkspaceCatalog): EditableDraft {
  const groupKeyById = new Map(
    catalog.content.modifierGroups.map((group) => [group.id, entityKey(group)]),
  );
  return {
    categories: catalog.content.categories.map((category) => ({
      items: category.items.map((item) => ({
        descriptionAr: item.description.ar ?? "",
        descriptionFr: item.description.fr ?? "",
        imageUrl: item.imageUrl ?? "",
        key: entityKey(item),
        modifierGroupKeys: item.modifierGroupIds
          .map((id) => groupKeyById.get(id))
          .filter((key): key is string => key !== undefined),
        nameAr: item.name.ar ?? "",
        nameFr: item.name.fr ?? "",
        priceMillimes: item.priceMillimes,
        sku: item.sku ?? "",
      })),
      key: entityKey(category),
      nameAr: category.name.ar ?? "",
      nameFr: category.name.fr ?? "",
    })),
    modifierGroups: catalog.content.modifierGroups.map((group) => ({
      key: entityKey(group),
      maxSelections: group.maxSelections,
      minSelections: group.minSelections,
      nameAr: group.name.ar ?? "",
      nameFr: group.name.fr ?? "",
      options: group.options.map((option) => ({
        key: entityKey(option),
        nameAr: option.name.ar ?? "",
        nameFr: option.name.fr ?? "",
        priceDeltaMillimes: option.priceDeltaMillimes,
      })),
    })),
  };
}

function entityKey(entity: { externalRef: string | null; id: string }): string {
  return entity.externalRef ?? `id:${entity.id}`;
}

function localizedName(ar: string, fr: string): { ar: string; fr: string } {
  const trimmedAr = ar.trim();
  return { ar: trimmedAr, fr: fr.trim() || trimmedAr };
}

function localizedText(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length >= 2 ? trimmed : null;
}

function itemChanged(
  item: EditableItem,
  match: CatalogItem,
  groupKeyById: Map<string, string>,
): boolean {
  if ((match.name.ar ?? "") !== item.nameAr.trim()) return true;
  if ((match.name.fr ?? match.name.ar ?? "") !== (item.nameFr.trim() || item.nameAr.trim()))
    return true;
  if (match.priceMillimes !== Math.round(item.priceMillimes)) return true;
  if ((match.description.ar ?? null) !== localizedText(item.descriptionAr)) return true;
  if ((match.description.fr ?? null) !== localizedText(item.descriptionFr)) return true;
  if ((match.imageUrl ?? null) !== (item.imageUrl.trim() || null)) return true;
  if ((match.sku ?? null) !== (item.sku.trim() || null)) return true;
  const publishedKeys = match.modifierGroupIds
    .map((id) => groupKeyById.get(id))
    .filter((key): key is string => key !== undefined);
  if (publishedKeys.length !== item.modifierGroupKeys.length) return true;
  return item.modifierGroupKeys.some((key) => !publishedKeys.includes(key));
}

function groupChanged(
  group: EditableGroup,
  match: WorkspaceCatalog["content"]["modifierGroups"][number],
): boolean {
  if ((match.name.ar ?? "") !== group.nameAr.trim()) return true;
  if ((match.name.fr ?? match.name.ar ?? "") !== (group.nameFr.trim() || group.nameAr.trim()))
    return true;
  if (match.minSelections !== group.minSelections || match.maxSelections !== group.maxSelections)
    return true;
  if (match.options.length !== group.options.length) return true;
  const optionsByKey = new Map(match.options.map((option) => [entityKey(option), option]));
  return group.options.some((option) => {
    const candidate = optionsByKey.get(option.key);
    if (!candidate) return true;
    return (
      (candidate.name.ar ?? "") !== option.nameAr.trim() ||
      (candidate.name.fr ?? candidate.name.ar ?? "") !==
        (option.nameFr.trim() || option.nameAr.trim()) ||
      candidate.priceDeltaMillimes !== Math.round(option.priceDeltaMillimes)
    );
  });
}
